import * as React from "react";
import styled from "styled-components";

import { media } from "../../common/utils/style";
import Translate from "../../common/components/Translate";
import Icon from "../../common/components/Icon";
import AutoLink from "../../common/components/AutoLink";
import ImageComponent from "../../common/components/Image";
import { Section, SectionTitle } from "./Section";

type DescriptionImage = WtdModels.Partial<WtdModels.DescriptionImage, "id" | "url" | "caption">;
type CompanyDescription = WtdModels.Partial<
  WtdModels.CompanyDescription,
  "type" | "description",
  {
    images: DescriptionImage[];
  }
>;

type Company = WtdModels.Partial<WtdModels.Company, "company_name">;

export interface AboutDescriptionSectionModels {
  CompanyDescription: CompanyDescription;
  Company: Company;
}

interface Props {
  company: Company;
  description: CompanyDescription;
  editable?: boolean;
}

const AboutDescriptionSection: React.SFC<Props> = props => {
  const { company, description, editable } = props;
  const images = description.images;
  const hasContent = Boolean(description.description) || images;

  if (!editable && !hasContent) {
    return null;
  }

  return (
    <StyledSection>
      <SectionTitle>
        <Translate id={`company.${description.type}_description`} />
        {editable && (
          <EditIconWrapper href={`/enterprise/companies/${company.company_name}/edit`}>
            <Icon name="wt-icon-pencil" />
          </EditIconWrapper>
        )}
      </SectionTitle>
      {images && images.length > 0 ? (
        <ImagesWrapper>
          {images.map((image: DescriptionImage) => (
            <ImageWrapper key={image.id}>
              <StyledImage image={image} alt="" size="310x215#" background />
              <Caption>{image.caption}</Caption>
            </ImageWrapper>
          ))}
        </ImagesWrapper>
      ) : null}
      {description.description && (
        <Description>
          <AutoLink text={description.description} />
        </Description>
      )}
    </StyledSection>
  );
};

const StyledSection = styled(Section)`
  @media ${media.large} {
    width: 640px !important;
    margin-top: 80px;
  }
`;

const EditIconWrapper = styled.a`
  margin-left: auto;
  display: block;
  color: rgba(0, 0, 0, 0.4) !important;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.6;
  }
`;

const ImagesWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  @media ${media.large} {
    margin-bottom: 40px;
  }
`;

const ImageWrapper = styled.div`
  margin-right: 2px;
  width: 50%;
  &:last-child {
    margin-right: 0;
  }
  @media ${media.large} {
    width: 310px;
    margin-right: 0;
  }
`;

const StyledImage = styled(ImageComponent)`
  width: 100%;
  height: 215px;
  background-size: cover;
  background-position: center;
`;

const Caption = styled.div`
  display: none;
  @media ${media.large} {
    display: block;
    margin-top: 10px;
    font-size: 11px;
    line-height: 1.64;
    color: rgba(0, 0, 0, 0.4);
  }
`;

const Description = styled.p`
  width: 100%;
  font-size: 13px;
  line-height: 1.85;
  color: rgba(0, 0, 0, 0.7);
  white-space: pre-wrap;
  @media ${media.large} {
    font-size: 14px;
    line-height: 1.71;
  }
`;

export default AboutDescriptionSection;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/AboutDescriptionSection.tsx